import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import { isEditableUserDefinition } from './watchlistAttributeDefinitions';
import { OwnGroupAttributeControls } from './WatchlistOwnGroupStatistics';
import { SavedAttributeAiRecipe, useSavedAttributeAiRecipes } from './WatchlistAttributeAiMaintenance';
import { attributeDraftOperations, attributeDraftValidation, changedAttributePreferences, metricPreference } from './watchlistAttributeManagerDraft';

const ROW_COLUMNS = '32px minmax(180px, 1fr) minmax(280px, 1.2fr) 36px';
const moveIds = (ids, id, target) => {
    const result = [...ids];
    result.splice(result.indexOf(id), 1);
    result.splice(ids.indexOf(target), 0, id);
    return result;
};
const captureDraft = (owner, revision, rows, preferences) => ({
    owner, revision, baseRows: rows.map((row) => ({ ...row })), rows: rows.map((row) => ({ ...row })),
    basePreferences: Object.fromEntries(rows.map(({ attribute_definition_id: id }) => [id, metricPreference(preferences[id])])),
    preferences: Object.fromEntries(rows.map(({ attribute_definition_id: id }) => [id, metricPreference(preferences[id])])),
});

export default function WatchlistAttributeManagerDialog({
    open, definitions, busy, error, onClose, onApply, onCreateWithAi,
    ownGroupPreferences = {}, onOwnGroupChange = null, userId = '', watchlistRevisionId = '',
    onFillBlanksWithAi = null, maintenanceBlockedReason = '',
}) {
    const ordered = useMemo(() => (definitions || []).filter(isEditableUserDefinition).sort(
        (left, right) => Number(left.display_order) - Number(right.display_order),
    ), [definitions]);
    const [draft, setDraft] = useState(() => captureDraft(userId, watchlistRevisionId, ordered, ownGroupPreferences));
    const [newName, setNewName] = useState('');
    const [retireTarget, setRetireTarget] = useState(null);
    const [discardOpen, setDiscardOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [drag, setDrag] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const opened = useRef({ open: false, owner: '' });
    const ownerRef = useRef(userId);
    ownerRef.current = userId;
    const instructionsId = useId();
    const maintenance = useSavedAttributeAiRecipes({ open, owner: userId, revision: watchlistRevisionId,
        enabled: Boolean(onFillBlanksWithAi) });

    useEffect(() => {
        if (!open) { opened.current.open = false; return; }
        if (opened.current.open && opened.current.owner === userId) return;
        opened.current = { open: true, owner: userId };
        setDraft(captureDraft(userId, watchlistRevisionId, ordered, ownGroupPreferences));
        setNewName(''); setRetireTarget(null); setDiscardOpen(false); setSaveError('');
        setDrag(null); setAnnouncement(''); setSaving(false);
    }, [open, userId, watchlistRevisionId, ordered, ownGroupPreferences]);

    // Raw input in the Add field is included on Save, even if Add was not clicked.
    const rowsForSave = newName.trim() ? [...draft.rows, {
        attribute_definition_id: 'draft:pending', display_name: newName, isNew: true,
    }] : draft.rows;
    const operations = attributeDraftOperations(draft.baseRows, rowsForSave);
    const preferenceChanges = changedAttributePreferences(draft.baseRows, rowsForSave,
        draft.basePreferences, draft.preferences);
    const dirty = operations.length > 0 || preferenceChanges.length > 0;
    const contextChanged = draft.owner !== userId || draft.revision !== watchlistRevisionId
        || JSON.stringify(draft.baseRows.map(({ attribute_definition_id, display_name }) => [attribute_definition_id, display_name]))
            !== JSON.stringify(ordered.map(({ attribute_definition_id, display_name }) => [attribute_definition_id, display_name]));
    const validation = attributeDraftValidation(rowsForSave);
    const locked = busy || saving || contextChanged;
    const aiBlockedReason = contextChanged ? 'The Watchlist changed. Cancel and reopen this editor.'
        : dirty ? 'Save or cancel pending changes in this editor before using AI.' : maintenanceBlockedReason;
    const displayed = drag?.keyboard
        ? drag.ids.map((id) => draft.rows.find((row) => row.attribute_definition_id === id)).filter(Boolean)
        : draft.rows;

    function requestClose() {
        if (locked && (busy || saving)) return;
        if (dirty) setDiscardOpen(true);
        else onClose();
    }
    function undoChanges() {
        if (locked || !dirty) return;
        setDraft(captureDraft(draft.owner, draft.revision, draft.baseRows, draft.basePreferences));
        setNewName(''); setRetireTarget(null); setDiscardOpen(false); setSaveError('');
        setDrag(null); setAnnouncement('Changes undone.');
    }
    async function save() {
        if (locked || !dirty || validation) return;
        const owner = userId;
        setSaving(true); setSaveError('');
        try {
            if (operations.length && !await onApply(operations)) return;
            if (ownerRef.current !== owner) return;
            for (const [id, preference] of preferenceChanges) onOwnGroupChange?.(id, preference);
            onClose();
        } catch {
            if (ownerRef.current === owner) setSaveError('Changes could not be saved. Your edits are still here.');
        } finally {
            if (ownerRef.current === owner) setSaving(false);
        }
    }
    function add() {
        if (locked || !newName.trim() || draft.rows.length >= 20) return;
        const row = { attribute_definition_id: `draft:${Math.random().toString(36).slice(2)}`, display_name: newName.trim(), isNew: true };
        setDraft((current) => ({ ...current, rows: [...current.rows, row] }));
        setNewName('');
    }
    const startReorder = (id, keyboard) => {
        setDrag({ id, ids: draft.rows.map((row) => row.attribute_definition_id), keyboard, target: id });
        setAnnouncement('Attribute picked up. Use the arrow keys to move, Enter to place, or Escape to cancel.');
    };
    const finishReorder = (ids) => {
        setDrag(null);
        if (locked) return;
        setDraft((current) => ({ ...current, rows: ids.map((id) => current.rows.find((row) => row.attribute_definition_id === id)) }));
        setAnnouncement('Order staged. Use Save changes to apply it.');
    };
    const reorderKeyDown = (event, id) => {
        if (locked) return;
        if (event.key === 'Escape' && drag?.keyboard) {
            event.preventDefault(); event.stopPropagation(); setDrag(null); setAnnouncement('Reordering cancelled.');
        } else if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            if (drag?.keyboard && drag.id === id) finishReorder(drag.ids);
            else startReorder(id, true);
        } else if (drag?.keyboard && drag.id === id && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
            event.preventDefault();
            const target = drag.ids.indexOf(id) + (event.key === 'ArrowUp' ? -1 : 1);
            if (target < 0 || target >= drag.ids.length) return;
            setDrag({ ...drag, ids: moveIds(drag.ids, id, drag.ids[target]) });
            setAnnouncement(`Position ${target + 1} of ${drag.ids.length}. Press Enter to place or Escape to cancel.`);
        } else if (event.key === 'Tab' && drag?.keyboard) setDrag(null);
    };
    function remove(row) {
        setDraft((current) => ({ ...current, rows: current.rows.filter((item) => item.attribute_definition_id !== row.attribute_definition_id) }));
        setRetireTarget(null);
    }

    return <>
        <Dialog open={open} onClose={requestClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxWidth: 1040 } }}>
            <DialogTitle>Manage Watchlist attributes</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 1.5, pt: '12px !important' }}>
                <Typography variant="body2" color="text.secondary">Add or organize categorical columns and choose statistics within each attribute group.</Typography>
                <Typography variant="caption" color="text.secondary">Changes stay in this editor until you save. Statistics recalculate after saving and closing.</Typography>
                {contextChanged && <Alert severity="warning">The Watchlist changed while this editor was open. Cancel and reopen it before saving.</Alert>}
                {(error || saveError) && <Alert severity="warning">{error || saveError}</Alert>}
                {maintenance.error && <Alert severity="warning">{maintenance.error}</Alert>}
                <Stack component="form" direction={{ xs: 'column', sm: 'row' }} spacing={1}
                    onSubmit={(event) => { event.preventDefault(); add(); }}>
                    <TextField size="small" fullWidth label="New attribute name" value={newName}
                        disabled={locked || draft.rows.length >= 20} inputProps={{ maxLength: 80 }}
                        onChange={(event) => setNewName(event.target.value)} />
                    <Button type="submit" variant="contained" startIcon={<AddIcon />}
                        disabled={locked || !newName.trim() || draft.rows.length >= 20} sx={{ flexShrink: 0 }}>Add</Button>
                    {onCreateWithAi && <Tooltip title={aiBlockedReason || 'Create a categorical column with AI'}>
                        <span><Button variant="outlined" startIcon={<AutoAwesomeIcon />}
                            disabled={locked || Boolean(aiBlockedReason) || draft.rows.length >= 20} onClick={onCreateWithAi}
                            sx={{ flexShrink: 0, whiteSpace: 'nowrap', height: '100%' }}>Add with AI</Button></span>
                    </Tooltip>}
                </Stack>
                {validation && <Alert severity="warning">{validation}</Alert>}
                <Box id={instructionsId} sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}>
                    Drag the handle to reorder. With the keyboard, press Space or Enter to pick up, use Up or Down to move,
                    and press Enter to place or Escape to cancel. Reordering remains a draft until Save changes.
                </Box>
                <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>{announcement}</Box>
                {!draft.rows.length && <Typography variant="body2" color="text.secondary">No attribute columns are defined yet.</Typography>}
                {draft.rows.length > 0 && <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: ROW_COLUMNS, gap: 1.5, px: 1, color: 'text.secondary' }}>
                    <span /><Typography variant="caption">Attribute name</Typography><Typography variant="caption">Within-group statistics</Typography><span />
                </Box>}
                <Stack spacing={0.75}>
                    {displayed.map((row, index) => {
                        const id = row.attribute_definition_id;
                        const recipe = !row.isNew && maintenance.recipes.get(id);
                        const baseName = draft.baseRows.find((item) => item.attribute_definition_id === id)?.display_name;
                        const dropTarget = drag && !drag.keyboard && drag.target === id && drag.id !== id;
                        return <Box key={id} role="group" aria-label={`${row.display_name || 'Unnamed'} attribute`}
                            onDragOver={(event) => {
                                if (!drag || drag.keyboard || locked) return;
                                event.preventDefault(); event.dataTransfer.dropEffect = 'move';
                                if (drag.target !== id) setDrag({ ...drag, target: id });
                            }}
                            onDrop={(event) => {
                                if (!drag || drag.keyboard || locked) return;
                                event.preventDefault(); finishReorder(moveIds(drag.ids, drag.id, id));
                            }}
                            sx={{ display: 'grid', alignItems: 'center', gap: 1.5, p: 1,
                                gridTemplateColumns: { xs: '28px minmax(0, 1fr) 36px', md: ROW_COLUMNS },
                                border: '1px solid', borderColor: dropTarget || drag?.id === id ? 'primary.main' : 'divider',
                                borderRadius: 1.5, bgcolor: drag?.id === id ? 'action.selected' : 'background.paper' }}>
                            <Tooltip title="Drag to reorder · Space to use keyboard"><span>
                                <IconButton size="small" aria-label={`Reorder ${row.display_name}`} aria-describedby={instructionsId}
                                    aria-pressed={drag?.id === id} disabled={locked || draft.rows.length < 2} draggable={!locked && draft.rows.length > 1}
                                    onDragStart={(event) => {
                                        event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', id); startReorder(id, false);
                                    }} onDragEnd={() => setDrag(null)} onKeyDown={(event) => reorderKeyDown(event, id)}
                                    onBlur={() => { if (drag?.keyboard) setDrag(null); }}
                                    sx={{ cursor: locked ? 'default' : drag?.id === id ? 'grabbing' : 'grab' }}>
                                    <DragIndicatorIcon fontSize="small" />
                                </IconButton>
                            </span></Tooltip>
                            <TextField size="small" fullWidth label={`Attribute ${index + 1}`} value={row.display_name}
                                disabled={locked} inputProps={{ maxLength: 80 }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') { event.preventDefault(); setAnnouncement('Name staged. Use Save changes to apply it.'); }
                                    if (event.key === 'Escape') {
                                        event.preventDefault(); event.stopPropagation();
                                        if (baseName != null) setDraft((current) => ({ ...current,
                                            rows: current.rows.map((item) => item.attribute_definition_id === id ? { ...item, display_name: baseName } : item) }));
                                    }
                                }}
                                InputProps={{ startAdornment: recipe ? <InputAdornment position="start">
                                    <SavedAttributeAiRecipe recipe={recipe} disabled={locked}
                                        blockedReason={aiBlockedReason} onFillBlanks={onFillBlanksWithAi} />
                                </InputAdornment> : null }}
                                onChange={(event) => {
                                    const display_name = event.target.value;
                                    setDraft((current) => ({ ...current, rows: current.rows.map((item) => item.attribute_definition_id === id ? { ...item, display_name } : item) }));
                                }} />
                            <Box sx={{ minWidth: 0, gridColumn: { xs: 2, md: 3 }, gridRow: { xs: 2, md: 1 } }}>
                                {row.isNew ? <Typography variant="caption" color="text.secondary">Save the column before choosing statistics.</Typography>
                                    : onOwnGroupChange ? <OwnGroupAttributeControls definition={row} preference={draft.preferences[id]}
                                        onChange={(attributeId, value) => setDraft((current) => ({ ...current,
                                            preferences: { ...current.preferences, [attributeId]: metricPreference(value) } }))} disabled={locked} />
                                        : <Typography variant="body2" color="text.secondary">Statistics unavailable</Typography>}
                            </Box>
                            <Tooltip title={row.isNew ? 'Remove new column' : 'Retire attribute'}>
                                <Box component="span" sx={{ gridColumn: { xs: 3, md: 4 }, gridRow: 1 }}>
                                    <IconButton size="small" color="error" aria-label={`${row.isNew ? 'Remove' : 'Retire'} ${row.display_name}`}
                                        disabled={locked} onClick={() => row.isNew ? remove(row) : setRetireTarget(row)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                </Box>
                            </Tooltip>
                        </Box>;
                    })}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy || saving}>Cancel</Button>
                <Button startIcon={<UndoIcon />} onClick={undoChanges} disabled={locked || !dirty}>Undo changes</Button>
                <Button variant="contained" onClick={() => void save()} disabled={locked || !dirty || Boolean(validation)}>
                    {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={open && Boolean(retireTarget)} onClose={busy || saving ? undefined : () => setRetireTarget(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Retire attribute?</DialogTitle>
            <DialogContent><Typography variant="body2">Remove “{retireTarget?.display_name}” when you save changes? Historical revisions keep the column and its values.</Typography></DialogContent>
            <DialogActions>
                <Button onClick={() => setRetireTarget(null)} disabled={busy || saving}>Cancel</Button>
                <Button color="error" onClick={() => remove(retireTarget)} disabled={locked}>Remove from draft</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={open && discardOpen} onClose={busy || saving ? undefined : () => setDiscardOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Discard attribute changes?</DialogTitle>
            <DialogContent><Typography variant="body2">Your column and statistics changes have not been saved.</Typography></DialogContent>
            <DialogActions><Button onClick={() => setDiscardOpen(false)} disabled={busy || saving}>Keep editing</Button>
                <Button color="error" onClick={onClose} disabled={busy || saving}>Discard changes</Button></DialogActions>
        </Dialog>
    </>;
}

WatchlistAttributeManagerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    definitions: PropTypes.arrayOf(PropTypes.shape({ attribute_definition_id: PropTypes.string.isRequired,
        display_name: PropTypes.string.isRequired, display_order: PropTypes.number.isRequired, editable: PropTypes.bool })).isRequired,
    busy: PropTypes.bool.isRequired, error: PropTypes.string, onClose: PropTypes.func.isRequired, onApply: PropTypes.func.isRequired,
    onCreateWithAi: PropTypes.func, ownGroupPreferences: PropTypes.object, onOwnGroupChange: PropTypes.func,
    userId: PropTypes.string, watchlistRevisionId: PropTypes.string, onFillBlanksWithAi: PropTypes.func, maintenanceBlockedReason: PropTypes.string,
};
